import React, { useState } from "react";
import Modal from "ui/Modal";
import { Drawer, DrawerContent } from "ui/Drawer";
import { useUser } from "util/UserContext";
import TextEditor from "components/TextEditor";
import InputTextArea from "ui/InputTextArea";
import { Bell, BellRing, Loader2 } from "lucide-react";
// Switch component temporarily implemented as checkbox
import api from "config/axiosConfig";
import { toast } from "sonner";
import { apiRoutes } from "util/apiRoutes";

interface SendNotificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  recipientId: number;
  recipientUsername: string;
  defaultMessage?: string;
  notificationType: string;
  entityType?: string;
  entityId?: number;
  relEntityType?: string;
  relEntityId?: number;
  properties?: Record<string, any>;
  buttonText?: string;
  title?: string;
  useTextArea?: boolean;
}

const SendNotificationModal: React.FC<SendNotificationModalProps> = ({
  isOpen,
  onClose,
  recipientId,
  recipientUsername,
  defaultMessage = "",
  notificationType,
  entityType,
  entityId,
  relEntityType,
  relEntityId,
  properties = {},
  buttonText = "Send notification",
  title = "Send notification",
  useTextArea = false
}) => {
  const { currentUser, setShowAuthModal, userIsMobile } = useUser();
  const [message, setMessage] = useState(defaultMessage);
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!currentUser) {
      setShowAuthModal(true);
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      const messageKey = notificationType === "share_perspective" ? "actor_message" : "message";
      const payload = {
        userId: recipientId,
        type: notificationType,
        entityType,
        entityId,
        relEntityType,
        relEntityId,
        properties: {
          ...properties,
          [messageKey]: message.trim() || undefined,
          senderUsername: isAnonymous ? undefined : currentUser.username
        },
        is_anonymous: isAnonymous
      };

      await api.post(apiRoutes.notifications.root, payload);
      
      toast.success(`Notification sent to ${recipientUsername}!`);
      onClose();
      setMessage("");
      setIsAnonymous(false);
    } catch (err: any) {
      console.error('Error sending notification:', err);
      setError(err.response?.data?.message || 'Failed to send notification. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const modalTitle = (
    <div className="flex items-center">
      {isAnonymous ? (
        <Bell className="inline-block mr-2 text-gray-600" />
      ) : (
        <BellRing className="inline-block mr-2 text-blue-600" />
      )}
      {title}
    </div>
  );

  const content = (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <p className="text-sm text-eoe-text dark:text-eoe-dark-text mb-2">
          Notify <span className="font-semibold">@{recipientUsername}</span>
        </p>
      </div>

      <div>
        <label className="block text-sm font-semibold mb-2">
          Message (optional)
        </label>
        {useTextArea ? (
          <InputTextArea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Add a personal message..."
            rows={4}
            maxLength={500}
          />
        ) : (
          <TextEditor
            value={message}
            onChange={setMessage}
            editorType="entity"
            placeholder="Add a personal message..."
          />
        )}
      </div>

      <div className="flex items-center justify-between p-3 bg-eoe-bg-hover dark:bg-eoe-dark-bg-hover rounded-lg">
        <div>
          <label className="text-sm font-medium text-eoe-text dark:text-eoe-dark-text">
            Send anonymously
          </label>
          <p className="text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">
            Your identity will be hidden from the recipient, but they will only see an in-app notification.
          </p>
        </div>
        <input
          type="checkbox"
          checked={isAnonymous}
          onChange={(e) => setIsAnonymous(e.target.checked)}
          className="toggle-switch-input"
        />
      </div>

      {error && (
        <div className="p-3 bg-red-100 dark:bg-red-900/20 rounded-md text-red-700 dark:text-red-400 text-sm">
          {error}
        </div>
      )}
    </form>
  );

  const footer = (
    <div className="flex flex-col space-y-2 sm:space-y-0 sm:flex sm:flex-row sm:justify-end sm:gap-x-2 py-2">
      <button
        type="button"
        onClick={onClose}
        className="btn-secondary"
        disabled={isSubmitting}
      >
        Cancel
      </button>
      <button
        type="submit"
        form="notification-form"
        disabled={isSubmitting}
        className={isSubmitting ? 'btn-primary-disabled' : 'btn-primary'}
        onClick={handleSubmit}
      >
        {isSubmitting ? <Loader2 className="size-4 animate-spin" /> : buttonText}
      </button>
    </div>
  );

  if (userIsMobile) {
    return (
      <Drawer
        open={isOpen}
        onOpenChange={(open) => {
          if (!open) {
            onClose();
          }
        }}
      >
        <DrawerContent header={modalTitle} footer={footer} stickyFooter={true}>
          <div className="p-4">
            {content}
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      title={modalTitle}
      footer={footer}
      stickyFooter={true}
    >
      <div className="p-4">
        {content}
      </div>
    </Modal>
  );
};

export default SendNotificationModal;